import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card.tsx';
import InitialsAvatar from '@/components/ui/InitialsAvatar.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { PublicClientDto } from '@/features/public/bookingSummary/types/BookingPublicSummaryDto.ts';
import DetailRow from './DetailRow';

interface ClientTileProps extends React.HTMLAttributes<HTMLDivElement> {
  client: PublicClientDto;
}

/** Adres w jednej linii; `null`, gdy nie ma z czego go złożyć. */
const addressOf = (client: PublicClientDto): string | null => {
  const line = [client.postalCode, client.city].filter(Boolean).join(' ');
  const parts = [client.street, line].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * Kafel osoby zapisującej — ten sam układ, co u organizatora, tylko że tu klient patrzy na własne
 * dane. Stoi nad uczestnikami, bo to on odpowiada za rezerwację i to do niego idą wiadomości.
 *
 * Rola nazwana wprost („Ty · płatnik”), bo strona zwraca się do tej osoby bezpośrednio — słowo
 * „zapisujący” należy do ekranów operatora.
 */
const ClientTile = ({ client, className, ...props }: ClientTileProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();

  const address = addressOf(client);
  const phone = client.phoneNumber
    ? `${client.phoneNumber.dialCode} ${client.phoneNumber.number}`
    : null;

  return (
    <Card className={cn('overflow-hidden', className)} {...props}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
      >
        <InitialsAvatar
          initials={`${client.firstName[0]}${client.lastName[0]}`}
          tone="outline"
          size="md"
        />
        <span className="min-w-0">
          <span className="block truncate text-[15px] font-bold text-ink">
            {client.firstName} {client.lastName}
          </span>
          <span className="block truncate text-sm text-neutral-500">
            Ty · płatnik
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            'ml-auto size-4 shrink-0 text-neutral-400 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div id={panelId} className="border-t border-neutral-100 px-4 py-3.5">
          <div className={columnLabelClass}>Dane kontaktowe</div>
          <div className="mt-1">
            <DetailRow label="Adres e-mail">{client.email}</DetailRow>
            {phone && <DetailRow label="Numer telefonu">{phone}</DetailRow>}
            {address && <DetailRow label="Adres">{address}</DetailRow>}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ClientTile;
